/** @jsx jsx */
import { Card, Colors, ICardProps } from '@blueprintjs/core';
import { css, jsx } from '@emotion/core';
import React from 'react';

type ContentCardHeaderProps = {
  leftElement?: React.ReactNode;
  rightElement?: React.ReactNode;
  children?: React.ReactNode;
};

export const ContentCardHeader = ({ leftElement, rightElement, children }: ContentCardHeaderProps) => {
  if (!leftElement && !rightElement && !children) {
    return null;
  }

  if (children) {
    return <div css={styles.header}>{children}</div>;
  }

  return (
    <div css={styles.header}>
      <div css={styles.headerLeft}>{leftElement}</div>
      <div css={styles.headerRight}>{rightElement}</div>
    </div>
  );
};

type ContentCardFooterProps = {
  leftElement?: React.ReactNode;
  rightElement?: React.ReactNode;
  children?: React.ReactNode;
};

export const ContentCardFooter = ({ leftElement, rightElement, children }: ContentCardFooterProps) => {
  if (!leftElement && !rightElement && !children) {
    return null;
  }

  if (children) {
    return <div css={styles.footer}>{children}</div>;
  }

  return (
    <div css={styles.footer}>
      <div css={styles.footerLeft}>{leftElement}</div>
      <div css={styles.footerRight}>{rightElement}</div>
    </div>
  );
};

type ContentCardScrollProps = {
  noPadding?: boolean;
  noFooter?: boolean;
  children?: React.ReactNode;
};

export const ContentCardScroll = ({ children, noPadding, noFooter }: ContentCardScrollProps) => (
  <div css={[styles.scrollContent, noPadding && styles.noPadding, noFooter && styles.noFooter]}>{children}</div>
);

type ContentCardIframeProps = {
  noFooter?: boolean;
  title: string;
  src: string;
};

export const ContentCardIframe = ({ noFooter, title, src }: ContentCardIframeProps) => (
  <iframe css={[styles.scrollIframe, noFooter && styles.noFooterIframe]} title={title} src={src}></iframe>
);

export const ContentCard = ({ children, ...rest }: ICardProps) => (
  <Card css={styles.card} {...rest}>
    {children}
  </Card>
);

const styles = {
  card: css`
    padding: 0px;
  `,
  header: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px ${Colors.LIGHT_GRAY1} solid;
  `,
  headerLeft: css`
    display: flex;
    align-items: center;
  `,
  headerRight: css`
    display: flex;
    justify-content: flex-end;
    align-items: center;
  `,
  scrollContent: css`
    background-color: ${Colors.LIGHT_GRAY5};
    flex-shrink: 1;
    padding: 20px;
    overflow-y: auto;
    max-height: calc(80vh - 126px); // 100vh - (2*10vh + 2*63px)
  `,
  scrollIframe: css`
    width: 100%;
    border: none;
    background-color: ${Colors.LIGHT_GRAY5};
    flex-shrink: 1;
    overflow-y: auto;
    height: calc(80vh - 126px); // 100vh - (2*10vh + 2*63px)
  `,
  noPadding: css`
    padding: 0px;
  `,
  noFooter: css`
    max-height: calc(80vh - 63px); // 100vh - (2*10vh + 1*63px)
  `,
  noFooterIframe: css`
    height: calc(80vh - 63px); // 100vh - (2*10vh + 1*63px)
  `,
  footer: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    border-top: 1px ${Colors.LIGHT_GRAY1} solid;
  `,
  footerLeft: css`
    display: flex;
  `,
  footerRight: css`
    display: flex;
    justify-content: flex-end;
  `,
};
